import React from 'react';
import Module from 'components/modules';
import getContainerSizeClass from "@root/helpers/get-container-size-class";
import getColorSanity from "@root/helpers/get-color-sanity";
import RichText from "components/rich-text";
import { getSanityMediaUrl } from '@root/helpers/get-sanity-media-url';

const GridBlock = ({ data }) => {
    const cells = data?.cell || [];
    const classes = getContainerSizeClass(data?.moduleSettings);
    const style = data?.style || 'default';
    const backgroundColor = data?.backgroundColor?.hex || 'transparent';
    const dividerColor = data?.dividerColor?.hex || 'transparent';
    const containerContent = data?.containerContent;
    const desktopColumns = data?.desktopColumns || 12;
    const tabletColumns = data?.tabletColumns || 8;
    const mobileColumns = data?.mobileColumns || 4;
    const mobileGap = data?.mobileGap;
    const tabletGap = data?.tabletGap;
    const desktopGap = data?.desktopGap;
    const backgroundImage = data?.backgroundImage;
    const backgroundSize = data?.backgroundSize || 'auto';
    const cardStyle = data?.cardStyle || 'default';
    const styleClass = () => {
        const styles = {
            "default": "",
            "divider": "grid-block--divider",
        }
        return styles[style] || styles.default;
    }

    const cardStyleClass = () => {
        const cardStyles = {
            "default": "",
            "card": "grid-block--card",
        }
        return cardStyles[cardStyle] || cardStyles.default;
    }

    const getBackgroundImage = () => {
        if (backgroundImage) {
            return getSanityMediaUrl(backgroundImage);
        }

        return null;
    }
    const backgroundImageUrl = getBackgroundImage();
    const hasBackgroundImage = Boolean(backgroundImageUrl);
    // Track cumulative positions for each breakpoint
    let desktopPosition = 0;
    let tabletPosition = 0;
    let mobilePosition = 0;

    return (
        <div
            className={`grid-block__wrapper ${hasBackgroundImage ? 'grid-block__wrapper--has-background-image' : ''} ${classes.join(" ")} `}
            style={{
                '--background-color': backgroundColor,
                backgroundImage: `url(${backgroundImageUrl})`,
                backgroundSize,
            }}
        >
            <div
                className={`grid-block ${containerContent ? "container" : ""} ${styleClass()} ${cardStyleClass()}`}
                style={{
                    '--grid-block-desktop-columns': desktopColumns,
                    '--grid-block-tablet-columns': tabletColumns,
                    '--grid-block-mobile-columns': mobileColumns,
                    ...(mobileGap !== undefined && { '--grid-block-mobile-gap': `${mobileGap}px` }),
                    ...(tabletGap !== undefined && { '--grid-block-tablet-gap': `${tabletGap}px` }),
                    ...(desktopGap !== undefined && { '--grid-block-desktop-gap': `${desktopGap}px` }),
                }}
            >
                {
                    cells.map((cell, cellIndex) => {
                        const module = cell?.module;
                        const modules = Array.isArray(module) ? module : (module ? [module] : []);

                        if (modules.length === 0) return null;

                        const desktopColSpan = cell?.desktopColSpan || 1;
                        const tabletColSpan = cell?.tabletColSpan || 1;
                        const mobileColSpan = cell?.mobileColSpan || 1;
                        const isLastCell = cellIndex === cells.length - 1;

                        // Calculate if this cell is the last in its row for each breakpoint
                        const isLastInDesktopRow = (desktopPosition + desktopColSpan) % desktopColumns === 0;
                        const isLastInTabletRow = (tabletPosition + tabletColSpan) % tabletColumns === 0;
                        const isLastInMobileRow = (mobilePosition + mobileColSpan) % mobileColumns === 0;

                        // Update positions for next iteration
                        desktopPosition = (desktopPosition + desktopColSpan) % desktopColumns;
                        tabletPosition = (tabletPosition + tabletColSpan) % tabletColumns;
                        mobilePosition = (mobilePosition + mobileColSpan) % mobileColumns;

                        const cellClasses = ['grid-block__cell'];
                        const verticalAlignment = cell?.verticalAlignment;
                        if (verticalAlignment) {
                            cellClasses.push(`grid-block__cell--vertical-${verticalAlignment}`);
                        }

                        const cellStyleVariant = cell?.style || 'default';
                        if (cellStyleVariant && cellStyleVariant !== 'default') {
                            cellClasses.push(`grid-block__cell--${cellStyleVariant}`);
                        }

                        if (style === 'divider') {
                            if (isLastInDesktopRow || isLastCell) {
                                cellClasses.push('grid-block__cell--no-border-desktop');
                            }
                            if (isLastInTabletRow || isLastCell) {
                                cellClasses.push('grid-block__cell--no-border-tablet');
                            }
                            if (isLastInMobileRow || isLastCell) {
                                cellClasses.push('grid-block__cell--no-border-mobile');
                            }
                        }

                        const cellStyle = {
                            '--grid-block-desktop-col-span': desktopColSpan,
                            '--grid-block-tablet-col-span': tabletColSpan,
                            '--grid-block-mobile-col-span': mobileColSpan,
                            '--divider-color': style === 'divider' ? dividerColor : 'transparent',
                        };

                        return (
                            <div key={cellIndex} className={cellClasses.join(' ')} style={cellStyle}>
                                {modules.map((moduleItem) => {
                                    if (!moduleItem || !moduleItem._type) return null;

                                    return (
                                        <Module
                                            key={moduleItem._key || cellIndex}
                                            type={moduleItem._type}
                                            moduleData={moduleItem}
                                        />
                                    );
                                })}
                            </div>
                        );
                    })
                }
            </div >
        </div>
    )
}

export default GridBlock;